import React, { useMemo, useState } from "react";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Info,
  TableProperties,
  Users
} from "lucide-react";
import { ProcessedScanResult, ScanResultType } from "../SpaceScanner.types";
import { getAreaNames, getReasonText } from "./SpaceScannerStage2Controller";

type SpaceScannerUnassignedSectionProps = {
  unassignedResults: ProcessedScanResult[];
};

type UnassignedGroup = {
  type: ScanResultType;
  results: ProcessedScanResult[];
  icon: React.ReactNode;
  suggestion: string;
};

/**
 * Returns an actionable suggestion based on the unassigned reason type
 */
const getSuggestion = (type: ScanResultType): string => {
  switch (type) {
    case "unassigned_no_tables_configured":
      return "Consider configuring tables for this booking type in venue settings.";
    case "unassigned_table_too_small":
      return "Consider adding larger tables or splitting the party across multiple bookings.";
    case "unassigned_rule_conflict":
      return "Review booking rules in venue settings to resolve conflicts.";
    case "unassigned_no_availability":
      return "Consider adjusting booking times or adding more tables for this time slot.";
    default:
      return "Review venue configuration to resolve this issue.";
  }
};

/**
 * Returns an appropriate icon for the unassigned reason type
 */
const getIcon = (type: ScanResultType): React.ReactNode => {
  switch (type) {
    case "unassigned_no_tables_configured":
      return (
        <TableProperties className="size-4 text-[var(--progressMed,#f59e0b)]" />
      );
    case "unassigned_table_too_small":
      return <Users className="size-4 text-[var(--progressMed,#f59e0b)]" />;
    case "unassigned_no_availability":
      return <Info className="size-4 text-[var(--progressMed,#f59e0b)]" />;
    case "unassigned_rule_conflict":
    default:
      return (
        <AlertTriangle className="size-4 text-[var(--progressMed,#f59e0b)]" />
      );
  }
};

/**
 * Component to display unassigned bookings grouped by reason with actionable suggestions
 */
export const SpaceScannerUnassignedSection = ({
  unassignedResults
}: SpaceScannerUnassignedSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const groupedResults = useMemo(() => {
    const groups: Map<ScanResultType, ProcessedScanResult[]> = new Map();

    for (const result of unassignedResults) {
      const existing = groups.get(result.result) ?? [];
      groups.set(result.result, [...existing, result]);
    }

    const groupedArray: UnassignedGroup[] = [];
    groups.forEach((results, type) => {
      groupedArray.push({
        type,
        results,
        icon: getIcon(type),
        suggestion: getSuggestion(type)
      });
    });

    return groupedArray;
  }, [unassignedResults]);

  if (unassignedResults.length === 0) {
    return null;
  }

  return (
    <section
      className="mb-4 rounded-lg border border-[var(--borderColor)] bg-[var(--pageBackgroundSecondary)] p-3"
      aria-label="Unassigned bookings"
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-color)] focus-visible:ring-offset-1 rounded"
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
      >
        <AlertTriangle className="size-5 flex-shrink-0 text-[var(--progressMed,#f59e0b)]" />
        <h3 className="flex-1 text-sm font-semibold text-[var(--primaryColor)]">
          {unassignedResults.length} Booking
          {unassignedResults.length !== 1 ? "s" : ""} Could Not Be Assigned
        </h3>
        {isExpanded ? (
          <ChevronUp
            className="size-4 flex-shrink-0 text-[var(--secondaryColor)]"
            aria-hidden="true"
          />
        ) : (
          <ChevronDown
            className="size-4 flex-shrink-0 text-[var(--secondaryColor)]"
            aria-hidden="true"
          />
        )}
      </button>

      {isExpanded && (
        <div className="mt-3 space-y-3">
          {groupedResults.map((group) => (
            <div
              key={group.type}
              className="rounded-md bg-[var(--panelBackground)] p-2.5"
            >
              <div className="mb-2 flex items-center gap-2">
                {group.icon}
                <span className="text-[13px] font-medium text-[var(--primaryColor)]">
                  {getReasonText(group.type)} ({group.results.length})
                </span>
              </div>

              <ul
                className="mb-2 ml-6 list-disc text-xs text-[var(--secondaryColor)]"
                aria-label={`Bookings with ${getReasonText(group.type)}`}
              >
                {group.results.map((result) => (
                  <li key={result.booking._id} className="mb-1 last:mb-0">
                    <span className="font-medium">
                      {result.booking.first_name} {result.booking.last_name}
                    </span>{" "}
                    ({result.booking.num_people} guest
                    {result.booking.num_people !== 1 ? "s" : ""})
                    {result.booking.time && (
                      <span className="text-[var(--tertiaryColor,#9ca3af)]">
                        {" "}
                        at {result.booking.time}
                      </span>
                    )}
                    {getAreaNames(result.previousAreas) && (
                      <span className="text-[var(--tertiaryColor,#9ca3af)]">
                        {" "}
                        - Currently: {getAreaNames(result.previousAreas)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>

              <p className="ml-6 text-xs italic text-[var(--secondaryColor)]">
                {group.suggestion}
              </p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
